import { useEffect } from 'react';
import { useController } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { useMergeRefs } from '@floating-ui/react';
import { AdvisorCreateMessageFormValues } from '../types';
import { File as GQLFile } from '../../../../generated/graphql';
import { useAdvisorCreateMessageForm } from './useAdvisorCreateMessageForm';
import { MessagingExpandableTagListItem } from '../../MessageExpandableTagList';

interface UseAdvisorCreateMessageFormFieldsArgs {
  useFormReturn: ReturnType<typeof useAdvisorCreateMessageForm>;
}

export function useAdvisorCreateMessageFormFields({ useFormReturn }: UseAdvisorCreateMessageFormFieldsArgs) {
  const { control, setValue, watch, trigger, handleSubmit, setFocus } = useFormReturn.formMethods;
  const [pendingFiles] = watch(['pendingFiles']);
  const [hasOnboardingReminder] = watch(['hasOnboardingReminder']);

  const tagItems: MessagingExpandableTagListItem[] = [
    ...pendingFiles.map((file) => ({ type: 'file' as const, file })),
    hasOnboardingReminder && { type: 'onboardingReminder' as const },
  ].filter(Boolean) as MessagingExpandableTagListItem[];

  const { field: messageField } = useController<AdvisorCreateMessageFormValues, 'message'>({
    control,
    name: 'message',
    // Either we send a non-empty message or we have at least one pending file or we have an onboarding reminder
    rules: {
      validate: (value, { pendingFiles, hasOnboardingReminder }) =>
        value.trim().length > 0 || pendingFiles.length > 0 || hasOnboardingReminder,
    },
  });

  function focusMessageBox() {
    setFocus('message', { shouldSelect: true });
  }

  const shortcutRef = useHotkeys('mod+enter', () => handleSubmit(useFormReturn.onSubmit)(), {
    enableOnFormTags: ['TEXTAREA'],
  });

  // We combine the ref so that react-hook-form can manage the textarea and react-hotkeys-hook can manage the hotkey scope
  const inputRef = useMergeRefs([messageField.ref, shortcutRef]);

  function onFileCreation(file: GQLFile) {
    setValue('pendingFiles', pendingFiles.concat(file));
  }

  function onRemovePendingFile(file: GQLFile) {
    const newFiles = pendingFiles?.filter(({ id }) => id !== file.id);
    setValue('pendingFiles', newFiles);
  }

  // Trigger validation when pending files change
  useEffect(() => {
    trigger('message');
  }, [pendingFiles.length]);

  return { inputRef, onFileCreation, onRemovePendingFile, messageField, focusMessageBox, tagItems };
}
